// Timeline strip, calendar grid and date picker — UI/UX §3.5, §3.6; React Aria for the calendar.
import { CalendarDate, getLocalTimeZone, today } from '@internationalized/date';
import {
  Button as AriaButton,
  CalendarGrid as AriaCalendarGrid,
  DatePicker as AriaDatePicker,
  Calendar,
  CalendarCell,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  DateInput,
  DateSegment,
  Dialog,
  Group,
  Heading,
  Label,
  Popover,
} from 'react-aria-components';
import { CalendarBlank, CaretLeft, CaretRight } from '../icons/index.js';
import { cx } from '../primitives/index.js';

export type MarkerKind = 'deadline' | 'drop' | 'occasion';

export interface DayMarker {
  /** YYYY-MM-DD */
  date: string;
  kind: MarkerKind;
  /** Screen-reader text, e.g. "Prompt deadline on October 29" (§4.4). */
  label: string;
  /** DOM id of the card this marker scrolls to (§3.5). */
  target?: string;
}

const MARKER_COLOR: Record<MarkerKind, string> = {
  deadline: 'var(--color-accent-primary)',
  drop: 'var(--color-accent-secondary)',
  occasion: 'var(--color-text-secondary)', // D-P19: warm-gray dots were ≈1.9:1 on cream
};

function daysOfMonth(yearMonth: string) {
  const [y, m] = yearMonth.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return Array.from({ length: last }, (_, i) => ({
    day: i + 1,
    iso: `${yearMonth}-${String(i + 1).padStart(2, '0')}`,
  }));
}

/** UI/UX §3.5 — horizontal date strip with dot markers; 44px hit areas. */
export function TimelineStrip({
  yearMonth,
  markers,
  todayIso,
  onSelect,
}: {
  yearMonth: string;
  markers: DayMarker[];
  todayIso?: string;
  onSelect?: (marker: DayMarker) => void;
}) {
  return (
    <div className="-mx-4 overflow-x-auto px-4" aria-label="Monthly timeline">
      <div className="flex gap-1">
        {daysOfMonth(yearMonth).map(({ day, iso }) => {
          const dayMarkers = markers.filter((m) => m.date === iso);
          const isToday = iso === todayIso;
          return (
            <button
              type="button"
              key={iso}
              onClick={() => dayMarkers[0] && onSelect?.(dayMarkers[0])}
              aria-label={
                dayMarkers.length > 0 ? dayMarkers.map((m) => m.label).join(', ') : `Day ${day}`
              }
              aria-current={isToday ? 'date' : undefined}
              className="flex min-h-[44px] w-11 shrink-0 flex-col items-center justify-center gap-1"
            >
              {/* §3.5 / §4.2 — 36px visual cell inside the 44px hit area. */}
              <span
                className={cx(
                  'type-caption flex h-9 w-9 items-center justify-center',
                  isToday && 'font-bold text-[var(--color-accent-primary)]',
                )}
              >
                {day}
              </span>
              <span className="flex h-[6px] items-center gap-[2px]">
                {dayMarkers.map((m) => (
                  <span
                    key={`${m.kind}-${m.label}`}
                    className="inline-block h-[6px] w-[6px] rounded-full"
                    style={{ background: MARKER_COLOR[m.kind] }}
                  />
                ))}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** UI/UX §3.6 — same markers, month grid, 44px cells. */
export function CalendarGrid({
  yearMonth,
  markers,
  onSelectDate,
}: {
  yearMonth: string;
  markers: DayMarker[];
  onSelectDate?: (iso: string) => void;
}) {
  const [y, m] = yearMonth.split('-').map(Number) as [number, number];
  return (
    <Calendar
      aria-label="Month"
      defaultFocusedValue={new CalendarDate(y, m, 1)}
      className="rounded-[var(--radius-md)] bg-[var(--color-bg-surface)] p-3"
      onChange={(value) => onSelectDate?.(value.toString())}
    >
      <header className="mb-2 flex items-center justify-between">
        <AriaButton
          slot="previous"
          aria-label="Previous month"
          className="flex min-h-[44px] min-w-[44px] items-center justify-center"
        >
          <CaretLeft size={20} />
        </AriaButton>
        <Heading className="type-heading" />
        <AriaButton
          slot="next"
          aria-label="Next month"
          className="flex min-h-[44px] min-w-[44px] items-center justify-center"
        >
          <CaretRight size={20} />
        </AriaButton>
      </header>
      <AriaCalendarGrid className="w-full">
        <CalendarGridHeader>
          {(day) => <CalendarHeaderCell className="type-label">{day}</CalendarHeaderCell>}
        </CalendarGridHeader>
        <CalendarGridBody>
          {(date) => {
            const iso = date.toString();
            const dayMarkers = markers.filter((mk) => mk.date === iso);
            return (
              <CalendarCell
                date={date}
                className="relative h-11 w-11 rounded-full text-center align-middle type-caption data-[today]:bg-[var(--color-today)] data-[today]:font-bold data-[selected]:outline data-[selected]:outline-2 data-[selected]:outline-[var(--color-accent-primary)]"
              >
                {({ formattedDate }) => (
                  <span className="flex h-full flex-col items-center justify-center gap-1">
                    {formattedDate}
                    <span className="flex gap-[2px]">
                      {dayMarkers.map((mk) => (
                        <span
                          key={mk.kind + mk.label}
                          className="inline-block h-[6px] w-[6px] rounded-full"
                          style={{ background: MARKER_COLOR[mk.kind] }}
                        />
                      ))}
                    </span>
                  </span>
                )}
              </CalendarCell>
            );
          }}
        </CalendarGridBody>
      </AriaCalendarGrid>
    </Calendar>
  );
}

/** Used for birthdays and occasion dates (App Flow §5, §6). */
export function DatePicker({
  label,
  value,
  onChange,
}: {
  label: string;
  value?: string | null;
  onChange: (iso: string | null) => void;
}) {
  const parsed = value ? parseIso(value) : null;
  return (
    <AriaDatePicker
      className="flex flex-col gap-1"
      value={parsed}
      onChange={(v) => onChange(v ? v.toString() : null)}
    >
      <Label className="type-caption">{label}</Label>
      <Group className="flex min-h-[48px] items-center justify-between rounded-[var(--radius-md)] border border-[var(--color-accent-warm-gray)] bg-[var(--color-bg-surface)] px-4">
        <DateInput className="flex type-body">
          {(segment) => <DateSegment segment={segment} className="px-[2px]" />}
        </DateInput>
        <AriaButton
          aria-label="Open calendar"
          className="flex min-h-[44px] min-w-[44px] items-center justify-center text-[var(--color-text-secondary)]"
        >
          <CalendarBlank size={22} />
        </AriaButton>
      </Group>
      <Popover className="rounded-[var(--radius-lg)] bg-[var(--color-bg-elevated)] p-3 shadow-[var(--shadow-elevated)]">
        <Dialog>
          <Calendar>
            <header className="mb-2 flex items-center justify-between">
              <AriaButton
                slot="previous"
                aria-label="Previous month"
                className="flex min-h-[44px] min-w-[44px] items-center justify-center"
              >
                <CaretLeft size={20} />
              </AriaButton>
              <Heading className="type-heading" />
              <AriaButton
                slot="next"
                aria-label="Next month"
                className="flex min-h-[44px] min-w-[44px] items-center justify-center"
              >
                <CaretRight size={20} />
              </AriaButton>
            </header>
            <AriaCalendarGrid>
              <CalendarGridHeader>
                {(day) => <CalendarHeaderCell className="type-label">{day}</CalendarHeaderCell>}
              </CalendarGridHeader>
              <CalendarGridBody>
                {(date) => (
                  <CalendarCell
                    date={date}
                    className="h-11 w-11 rounded-full text-center type-caption data-[today]:bg-[var(--color-today)] data-[today]:font-bold data-[selected]:bg-[var(--color-accent-primary)] data-[selected]:text-[var(--color-on-accent)]"
                  />
                )}
              </CalendarGridBody>
            </AriaCalendarGrid>
          </Calendar>
        </Dialog>
      </Popover>
    </AriaDatePicker>
  );
}

function parseIso(iso: string) {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new CalendarDate(y, m, d);
}

export function todayIso() {
  return today(getLocalTimeZone()).toString();
}
